import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { getApi } from "../lib/get-api";
import {
  brokenRuleBannerLabel,
  firstBrokenMember,
  Member,
  memberDisplayName,
  memberOpenPath,
  totalBrokenRules,
} from "../lib/member-access";
import { useAuth } from "../lib/useAuth";

const bannerClass =
  "flex min-h-11 w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded-xl border px-3 py-2 text-left text-sm no-underline";

const bannerStyle: React.CSSProperties = {
  borderColor: "color-mix(in srgb, var(--od-warning) 45%, transparent)",
  backgroundColor: "color-mix(in srgb, var(--od-warning) 10%, transparent)",
};

/**
 * The owner control for a row filter whose column is gone.
 * The label is the count. The name is the member the control opens.
 */
export const BrokenRuleBanner: React.FC<{
  label: string;
  memberName: string;
  href?: string;
  onClick?: () => void;
  className?: string;
}> = ({ label, memberName, href, onClick, className = "" }) => {
  const body = (
    <>
      <span className="font-medium text-od-text">{label}</span>
      <span className="text-od-muted">{memberName}</span>
    </>
  );
  if (href) {
    return (
      <Link
        to={href}
        data-testid="broken-rule-banner"
        className={`${bannerClass} ${className}`}
        style={bannerStyle}
      >
        {body}
      </Link>
    );
  }
  return (
    <button
      type="button"
      data-testid="broken-rule-banner"
      onClick={onClick}
      className={`${bannerClass} ${className}`}
      style={bannerStyle}
    >
      {body}
    </button>
  );
};

/**
 * Loads the member list and shows the banner when a rule is broken.
 * Only the owner can read that list. A 403 hides the banner.
 */
export const BrokenRuleBannerLoader: React.FC<{
  projectId: string;
  datasourceId: string;
  className?: string;
}> = ({ projectId, datasourceId, className }) => {
  const { token } = useAuth();
  const [target, setTarget] = useState<{
    id: string;
    name: string;
    count: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const api = getApi({ token });
    (api as any)
      .request({
        path: `/projects/${projectId}/invites`,
        method: "GET",
        format: "json",
      })
      .then((res: { data?: Member[] }) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        const member = firstBrokenMember(rows);
        const count = totalBrokenRules(rows);
        const label = brokenRuleBannerLabel(count);
        if (!member || !label) {
          setTarget(null);
          return;
        }
        setTarget({
          id: member.id,
          name: memberDisplayName(member),
          count,
        });
      })
      .catch(() => {
        if (!cancelled) setTarget(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, token]);

  if (!target) return null;
  const label = brokenRuleBannerLabel(target.count);
  if (!label) return null;
  return (
    <BrokenRuleBanner
      label={label}
      memberName={target.name}
      href={memberOpenPath(datasourceId, target.id)}
      className={className}
    />
  );
};
